import { useCallback, useState } from "react";
import { isInsidePortalPopover } from "@/lib/portalPopover";

interface Options {
  /** True when the form holds edits worth protecting. */
  isDirty: boolean;
  /** Performs the actual close (reset + `onOpenChange(false)`). */
  onConfirmedClose: () => void;
}

interface Result {
  /** Whether the "discard changes?" dialog is showing. */
  confirmOpen: boolean;
  setConfirmOpen: (open: boolean) => void;
  /** Wire to `Dialog.Root`'s `onOpenChange`. */
  requestClose: (open: boolean) => void;
  /** Wire to `onInteractOutside` / `onEscapeKeyDown` / `onPointerDownOutside`. */
  guardInteractOutside: (event: {
    target: EventTarget | null;
    preventDefault: () => void;
  }) => void;
  /** Call from the confirm dialog's destructive action. */
  confirmDiscard: () => void;
}

/**
 * The dirty-form close interception, shared by every Sprint 186 form surface.
 *
 * Two obligations, both won by shipped bugs:
 *
 * - **Unsaved changes** — closing a dirty form via ✕, Escape or an outside
 *   click asks before discarding. Today `CreateOrderModal` and
 *   `EditTemplateModal` both drop the edits silently.
 * - **BUG-497** — `DateInput` renders its calendar through a portal, so a click
 *   inside it is NOT a DOM descendant of the dialog and reads as an "outside"
 *   click. Without {@link isInsidePortalPopover}, picking a delivery date tears
 *   the whole dialog down mid-interaction. The guard must be driven by the REAL
 *   calendar in any test — a mocked one cannot reproduce the portal, so it would
 *   pass against code that has this bug.
 */
export function useUnsavedGuard({
  isDirty,
  onConfirmedClose,
}: Options): Result {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const requestClose = useCallback(
    (open: boolean) => {
      if (open) return;
      if (isDirty) {
        setConfirmOpen(true);
        return;
      }
      onConfirmedClose();
    },
    [isDirty, onConfirmedClose],
  );

  const guardInteractOutside = useCallback(
    (event: { target: EventTarget | null; preventDefault: () => void }) => {
      if (isInsidePortalPopover(event.target)) {
        event.preventDefault();
      }
    },
    [],
  );

  const confirmDiscard = useCallback(() => {
    setConfirmOpen(false);
    onConfirmedClose();
  }, [onConfirmedClose]);

  return {
    confirmOpen,
    setConfirmOpen,
    requestClose,
    guardInteractOutside,
    confirmDiscard,
  };
}
